// TROST-Web-App-Server는 firebase npm SDK 대신 gtag.js만 직접 로드해서 GA4로 보낸다.
// (레거시 node/webpack 환경 + 번들 사이즈 절감)
// 외부 API surface(getPlatformType / setPlatformType / setGaUserId / refreshGaUserId /
// sendFirebaseGAEvent)는 다른 레포의 firebaseAnalytics.ts와 동일하게 유지한다.

export type PlatformType =
  | 'pc_web'
  | 'mobile_web'
  | 'trost_app'
  | 'cashwalk_app';

type Runtime = 'web' | 'webview';

const isProd = process.env.NODE_ENV === 'production';

// TROST-FrontEnd와 동일한 trost-app-android Firebase 프로젝트의 measurementId
const MEASUREMENT_ID = isProd ? 'G-CDVXXHBN3N' : 'G-VESW7VK9SH';

let platformTypeCache: PlatformType | null = null;
let userIdCache: string | null = null;
let userPropertiesCache: Record<string, string> = {};
let gtagLoaded = false;

type TrostWindow = Window & {
  dataLayer?: any[];
  gtag?: (...args: any[]) => void;
  __TROST_GA4_LOADED__?: Record<string, boolean>;
  __TROST_GA4_PAGE_VIEW_SENT__?: Record<string, boolean>;
  __trostFirebaseObservedGtag?: (...args: any[]) => void;
};

const isAppPlatform = (type: PlatformType | null) =>
  type === 'trost_app' || type === 'cashwalk_app';

const detectPlatformType = (): PlatformType => {
  if (typeof window === 'undefined') return 'pc_web';
  const wn = window as any;
  const ua = (navigator.userAgent || '').toLowerCase();

  const isAppFromLocalStorage = safeGetLocalStorageItem('isApp') === 'true';

  // 쿠키 기반 판정 — app_version 쿠키 또는 localStorage isApp 신호가 함께 있을 때만 신뢰
  const cookieMatch = document.cookie.match(/(?:^|;\s*)app_type=([^;]+)/);
  const cookieAppType = cookieMatch?.[1];
  const appVersionMatch = document.cookie.match(/(?:^|;\s*)app_version=([^;]+)/);
  const hasAppVersion = !!appVersionMatch?.[1];
  const appGate = hasAppVersion || isAppFromLocalStorage;
  if (appGate && cookieAppType === 'cashwalk') return 'cashwalk_app';
  if (appGate && cookieAppType === 'trost') return 'trost_app';

  const hasCashwalkBridge = typeof wn.__CASHWALK_APP__?.fireBaseEvent === 'function';
  const hasTrostBridge = typeof wn.__TROST_APP__?.fireBaseEvent === 'function';
  const hasIosBridge = !!wn.webkit?.messageHandlers?.fireBaseEvent;

  if (hasCashwalkBridge) return 'cashwalk_app';
  if (hasTrostBridge) return 'trost_app';

  if (hasIosBridge) {
    if (cookieAppType === 'cashwalk') return 'cashwalk_app';
    const params = new URLSearchParams(window.location.search);
    const qpAppType = params.get('appType') || params.get('app_type');
    if (qpAppType === 'cashwalk') return 'cashwalk_app';
    return 'trost_app';
  }

  if (isAppFromLocalStorage) return 'trost_app';

  const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua);
  return isMobile ? 'mobile_web' : 'pc_web';
};

const getRuntime = (): Runtime => (isAppPlatform(platformTypeCache || detectPlatformType()) ? 'webview' : 'web');

function callGtag(...args: any[]) {
  if (typeof window === 'undefined') return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag === 'function') {
    w.gtag(...args);
  } else {
    // gtag가 아직 정의되기 전에도 큐로 쌓아두면 gtag.js 로드 후 정상 처리됨
    w.dataLayer.push(args);
  }
}

const safeGetLocalStorageItem = (key: string): string => {
  // Safari 시크릿 모드/차단된 iframe 등에서는 localStorage 접근 자체가 SecurityError를 던질 수 있음
  try {
    return window.localStorage?.getItem(key) || '';
  } catch {
    return '';
  }
};

type DebuggableParams<T extends Record<string, unknown>> = T & {
  debug_mode?: boolean;
};

const addDebugMode = <T extends Record<string, unknown>>(
  params: T,
): DebuggableParams<T> => {
  if (typeof window === 'undefined') return params;
  const isFirebaseAlert = safeGetLocalStorageItem('useStorage.FirebaseTaggingAlert.check') === 'true';
  if (!isProd && isFirebaseAlert) {
    return { ...params, debug_mode: true };
  }
  return params;
};

// Firebase SDK setUserProperties 기본 경로(non-global)와 동일 패턴:
//   gtag('config', measurementId, { update: true, user_properties: properties })
// 글로벌 'set'을 안 쓰는 이유: 페이지마다 UA-/AW- 인라인 gtag가 있어 set이 우리 measId에
// 적용된다는 보장이 없음 (Firebase SDK도 기본은 scoped config).
// update:true 필수: 같은 measId의 다른 필드(user_id 등)를 REPLACE하지 않도록 MERGE.
// send_page_view: false — 초기 자동 PV는 loadGtagScript의 최초 config에서 한 번만 발사.
const applyUserProperty = (key: string, value: string) => {
  userPropertiesCache = { ...userPropertiesCache, [key]: value };
  if (typeof window === 'undefined') return;
  callGtag(
    'config',
    MEASUREMENT_ID,
    addDebugMode({
      update: true,
      user_properties: { [key]: value },
      send_page_view: false,
      runtime: getRuntime(),
    }),
  );
};

const syncPlatformType = (nextType: PlatformType) => {
  if (platformTypeCache === nextType) return platformTypeCache;
  platformTypeCache = nextType;
  applyUserProperty('platform_type', nextType);
  return platformTypeCache;
};

export const getPlatformType = (): PlatformType => {
  const detected = detectPlatformType();

  if (!platformTypeCache) {
    platformTypeCache = detected;
    return platformTypeCache;
  }

  // 앱 브릿지는 페이지 JS보다 늦게 준비될 수 있다. 초기에 mobile_web/pc_web으로
  // 캐시됐더라도 이후 웹뷰 신호가 확인되면 user property를 다시 밀어 넣는다.
  if (!isAppPlatform(platformTypeCache) && isAppPlatform(detected)) {
    return syncPlatformType(detected);
  }

  return platformTypeCache;
};

// 페이지 내 다른 GA(UA-/AW-) 인라인 gtag가 동일 dataLayer를 공유하기 때문에,
// Firebase measurementId 대상 config 호출에 user_properties / user_id / debug_mode를
// 항상 강제로 부착하기 위해 window.gtag 자체를 래핑한다.
const installGtagConfigObserver = () => {
  if (typeof window === 'undefined') return;
  const w = window as TrostWindow;
  if (w.__trostFirebaseObservedGtag && w.gtag === w.__trostFirebaseObservedGtag) {
    return;
  }

  const originalGtag = typeof w.gtag === 'function'
    ? w.gtag
    : function gtagFallback(...args: any[]) {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer!.push(args);
    };

  w.gtag = function trostFirebaseObservedGtag(...args: any[]) {
    if (args[0] === 'config' && args[1] === MEASUREMENT_ID) {
      const merged = {
        ...(args[2] || {}),
        runtime: getRuntime(),
        user_properties: {
          ...((args[2] || {}).user_properties || {}),
          ...userPropertiesCache,
          platform_type: getPlatformType(),
        },
      };
      const withDebug = addDebugMode(merged);
      if (userIdCache && !withDebug.user_id) withDebug.user_id = userIdCache;
      const newArgs = [args[0], args[1], withDebug, ...args.slice(3)];
      return originalGtag.apply(this, newArgs);
    }

    return originalGtag.apply(this, args);
  };

  w.__trostFirebaseObservedGtag = w.gtag;
};

export const setPlatformType = (type: PlatformType) => {
  platformTypeCache = type;
  applyUserProperty('platform_type', type);
};

// 이 레포는 accessToken을 localStorage.auth.data.accessToken.token 경로에 중첩 저장한다
// (common-es6.js의 setJWTTokenData / getJWTLocalStorage 참고). cashwalk 웹뷰에서는
// window.__USER__.accessToken로 주입된다.
const readAccessTokenSync = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const cashwalkToken = (window as any)?.__USER__?.accessToken;
    if (cashwalkToken) return cashwalkToken;

    const qs = new URLSearchParams(window.location.search);
    const qsToken = qs.get('accessToken');
    if (qsToken) return qsToken;

    const authRaw = safeGetLocalStorageItem('auth');
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      return parsed?.data?.accessToken?.token || '';
    }
  } catch {
    /* noop */
  }
  return '';
};

export const setGaUserId = (userId: string | number | null | undefined) => {
  const str = userId === undefined || userId === null || userId === ''
    ? null
    : String(userId);
  if (userIdCache === str) return;
  userIdCache = str;
  if (typeof window === 'undefined') return;
  // GA4는 user_id: null을 reset 신호로 받지 않으므로, 로그아웃 시엔 빈 문자열로 명시 해제
  // update:true 필수: 빠지면 같은 measId의 user_properties 등 다른 필드가 함께 리셋됨.
  callGtag(
    'config',
    MEASUREMENT_ID,
    addDebugMode({
      update: true,
      send_page_view: false,
      runtime: getRuntime(),
      user_id: str ?? '',
    }),
  );
};

// 웹뷰 브릿지는 GA config를 거치지 않으므로 params에 직접 실어야 한다.
export const getGaUserId = (): string | null => userIdCache;

// common-es6.js의 OLD_API_URL / HYENA_API_URL과 동일한 호스트 기반 분기.
// /users/me는 trost proxy API에만 있다 (hyena·old-api는 404).
const getTrostProxyApiUrl = (): string => {
  const host = window.location.host;
  return host === 'trost.co.kr' || host === 'www.trost.co.kr'
    ? 'https://proxy-api.trost.co.kr'
    : 'https://test-proxy-api.trost.co.kr';
};

/**
 * `/users/me`를 조회해 GA4의 user_id(회원 번호)를 채운다.
 *
 * 이 엔트리 번들은 모든 페이지에 주입되므로 api.ts / common-es6.js를 import하지 않고
 * fetch를 직접 쓴다. 두 모듈을 끌어오면 번들이 페이지당 그만큼 무거워진다.
 */
export const refreshGaUserId = async () => {
  if (typeof window === 'undefined') return;

  const token = readAccessTokenSync();
  if (!token) {
    setGaUserId(null);
    return;
  }

  try {
    const res = await fetch(`${getTrostProxyApiUrl()}/users/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) return;

    const data = await res.json();
    setGaUserId(data?.no);
  } catch (e) {
    console.warn('[firebaseAnalytics] refreshGaUserId failed', e);
  }
};

const loadGtagScript = () => {
  if (typeof window === 'undefined' || gtagLoaded) return;
  const w = window as TrostWindow;
  w.__TROST_GA4_LOADED__ = w.__TROST_GA4_LOADED__ || {};
  if (w.__TROST_GA4_LOADED__[MEASUREMENT_ID]) {
    gtagLoaded = true;
    return;
  }

  w.dataLayer = w.dataLayer || [];
  // gtag가 이미 다른 스크립트로 정의되어 있을 수 있으니 덮어쓰지 않음
  if (typeof w.gtag !== 'function') {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }

  // observer를 가장 먼저 설치 — 이후 모든 firebase measId config 호출이 자동 enrich됨
  installGtagConfigObserver();

  const gtag = w.gtag as (...args: any[]) => void;
  gtag('js', new Date());

  const platformType = getPlatformType();
  userPropertiesCache = { ...userPropertiesCache, platform_type: platformType };

  // user_properties를 첫 config에 포함 → measurementId의 자동 PV에 부착.
  // - user_properties는 nested 형식 (Firebase SDK setUserProperties 기본 경로와 동일)
  // - update:true: 후속 applyUserProperty config가 다른 필드를 REPLACE하지 않고 MERGE
  // - send_page_view:false: 자동 page_view 중복을 피하고, 아래에서 필요한 값을 붙여 직접 1회 발사.
  const configParams: Record<string, any> = {
    update: true,
    send_page_view: false,
    runtime: getRuntime(),
    user_properties: { platform_type: platformType },
  };
  gtag('config', MEASUREMENT_ID, addDebugMode(configParams));

  w.__TROST_GA4_PAGE_VIEW_SENT__ = w.__TROST_GA4_PAGE_VIEW_SENT__ || {};
  if (!w.__TROST_GA4_PAGE_VIEW_SENT__[MEASUREMENT_ID]) {
    const pageViewParams: Record<string, any> = {
      send_to: MEASUREMENT_ID,
      page_location: window.location.href,
      page_title: document.title,
      platform_type: platformType,
    };
    gtag('event', 'page_view', addDebugMode(pageViewParams));
    w.__TROST_GA4_PAGE_VIEW_SENT__[MEASUREMENT_ID] = true;
  }

  const existing = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}"]`,
  );
  if (!existing) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);
  }
  gtagLoaded = true;
  w.__TROST_GA4_LOADED__[MEASUREMENT_ID] = true;
};

if (typeof window !== 'undefined') {
  loadGtagScript();
  // /users/me는 비동기라 위에서 발사한 page_view에는 붙지 않는다. 이후 이벤트와
  // 웹뷰 브릿지 params에는 부착된다.
  refreshGaUserId();
}

export const sendFirebaseGAEvent = (
  name: string,
  params?: Record<string, unknown>,
) => {
  if (!name || typeof window === 'undefined') return;
  try {
    callGtag(
      'event',
      name,
      addDebugMode({
        ...(params || {}),
        platform_type: getPlatformType(),
        // 페이지에 다른 GA(예: 마케팅용)가 config되어 있어도 firebase measurementId로 정확히 라우팅
        send_to: MEASUREMENT_ID,
      }),
    );
  } catch (e) {
    console.warn('[firebaseAnalytics] sendFirebaseGAEvent failed', e);
  }
};
